import {
  isMobileCheckoutEnvironment,
  type MobileCheckoutEnvironment,
} from "../../../shared/lib/environment";

export type KioskPlacement = "enter" | "exit";

export type KioskOperator = {
  userId: string;
  fullName: string;
};

export type KioskBindingV1 = {
  version: 1;
  kioskInstallationId: string;
  storeKey: string;
  storeName: string;
  zoneId: string;
  zoneName: string;
  placement: KioskPlacement;
  services: MobileCheckoutEnvironment;
  configuredAt: string;
  configuredBy: KioskOperator | null;
};

export type LegacyKioskBinding = Pick<
  KioskBindingV1,
  "storeKey" | "zoneId" | "placement"
>;

const BINDING_KEY = "kiosk_binding_v1";
const bindingListeners = new Set<() => void>();
let cachedBindingRaw: string | null | undefined;
let cachedBinding: KioskBindingV1 | null = null;
let storageListenerInstalled = false;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isKioskOperator(value: unknown): value is KioskOperator {
  if (!value || typeof value !== "object") return false;
  const operator = value as Record<string, unknown>;
  return (
    isNonEmptyString(operator.userId) && isNonEmptyString(operator.fullName)
  );
}

export function isKioskBinding(value: unknown): value is KioskBindingV1 {
  if (!value || typeof value !== "object") return false;
  const binding = value as Record<string, unknown>;

  return (
    binding.version === 1 &&
    isNonEmptyString(binding.kioskInstallationId) &&
    isNonEmptyString(binding.storeKey) &&
    isNonEmptyString(binding.storeName) &&
    isNonEmptyString(binding.zoneId) &&
    isNonEmptyString(binding.zoneName) &&
    (binding.placement === "enter" || binding.placement === "exit") &&
    isMobileCheckoutEnvironment(binding.services) &&
    isNonEmptyString(binding.configuredAt) &&
    (binding.configuredBy === null || isKioskOperator(binding.configuredBy))
  );
}

function normalizePersistedKioskBinding(
  value: unknown,
): KioskBindingV1 | null {
  if (isKioskBinding(value)) return value;
  if (!value || typeof value !== "object") return null;

  const legacy = value as Record<string, unknown>;
  if (
    !isNonEmptyString(legacy.kioskId) ||
    legacy.kioskInstallationId !== undefined
  ) {
    return null;
  }

  const { kioskId, ...rest } = legacy;
  const migrated = { ...rest, kioskInstallationId: kioskId };
  return isKioskBinding(migrated) ? migrated : null;
}

function refreshKioskBindingSnapshot(): KioskBindingV1 | null {
  try {
    const raw = localStorage.getItem(BINDING_KEY);
    if (raw === cachedBindingRaw) return cachedBinding;
    cachedBindingRaw = raw;
    if (!raw) {
      cachedBinding = null;
      return cachedBinding;
    }
    const parsed: unknown = JSON.parse(raw);
    const normalized = normalizePersistedKioskBinding(parsed);
    if (normalized) {
      const normalizedRaw = JSON.stringify(normalized);
      if (normalizedRaw !== raw) localStorage.setItem(BINDING_KEY, normalizedRaw);
      cachedBindingRaw = normalizedRaw;
      cachedBinding = normalized;
      return cachedBinding;
    }
    localStorage.removeItem(BINDING_KEY);
  } catch {
    // Corrupt or unavailable storage is treated as an unconfigured kiosk.
    try {
      localStorage.removeItem(BINDING_KEY);
    } catch {
      // Storage can itself be unavailable (for example in Safari privacy mode).
    }
  }
  cachedBindingRaw = null;
  cachedBinding = null;
  return cachedBinding;
}

export function readKioskBinding(): KioskBindingV1 | null {
  return refreshKioskBindingSnapshot();
}

export function getKioskBindingSnapshot(): KioskBindingV1 | null {
  return refreshKioskBindingSnapshot();
}

function notifyKioskBindingListeners(): void {
  bindingListeners.forEach((listener) => listener());
}

function onBindingStorage(event: StorageEvent): void {
  if (event.key !== null && event.key !== BINDING_KEY) return;
  const previous = cachedBinding;
  refreshKioskBindingSnapshot();
  if (cachedBinding !== previous) notifyKioskBindingListeners();
}

export function subscribeKioskBinding(listener: () => void): () => void {
  bindingListeners.add(listener);
  if (!storageListenerInstalled) {
    window.addEventListener("storage", onBindingStorage);
    storageListenerInstalled = true;
  }
  return () => {
    bindingListeners.delete(listener);
    if (bindingListeners.size === 0 && storageListenerInstalled) {
      window.removeEventListener("storage", onBindingStorage);
      storageListenerInstalled = false;
    }
  };
}

export function writeKioskBinding(binding: KioskBindingV1): void {
  if (!isKioskBinding(binding)) {
    throw new TypeError("Cannot persist an invalid kiosk binding");
  }
  const raw = JSON.stringify(binding);
  localStorage.setItem(BINDING_KEY, raw);
  cachedBindingRaw = raw;
  cachedBinding = binding;
  notifyKioskBindingListeners();
}

export function createKioskInstallationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `kiosk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function readLegacyKioskBinding(
  location: Pick<Location, "pathname" | "search"> = window.location,
): LegacyKioskBinding | null {
  const match = location.pathname.match(
    /^\/stores\/([^/]+)\/zones\/([^/]+)\/mobile-app\/?$/,
  );
  if (!match) return null;

  const placement = new URLSearchParams(location.search).get("placement");
  return {
    storeKey: decodeURIComponent(match[1]),
    zoneId: decodeURIComponent(match[2]),
    placement: placement === "enter" ? "enter" : "exit",
  };
}

export function createKioskBinding(input: {
  previous?: KioskBindingV1 | null;
  storeKey: string;
  storeName: string;
  zoneId: string;
  zoneName: string;
  placement: KioskPlacement;
  services: MobileCheckoutEnvironment;
  configuredBy: KioskOperator | null;
}): KioskBindingV1 {
  return {
    version: 1,
    kioskInstallationId:
      input.previous?.kioskInstallationId ?? createKioskInstallationId(),
    storeKey: input.storeKey,
    storeName: input.storeName,
    zoneId: input.zoneId,
    zoneName: input.zoneName,
    placement: input.placement,
    services: input.services,
    configuredAt: new Date().toISOString(),
    configuredBy: input.configuredBy,
  };
}
